// The games of an organization (spec/dashboard.md sections 2.3, 12.4): a row opens on a single click (`openOnClick`) and
// the name is a link to the game's home, so Ctrl-click opens it in a new tab.
import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { ApiError } from '../api/client';
import { ops } from '../api/operations';
import type { Game, Organization } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Chip';
import { DataGrid } from '../ui/DataGrid';
import { formatInstantWithZone } from '../ui/time';
import { Modal } from '../ui/Modal';
import { EmptyState } from '../ui/Banner';
import { Field } from '../ui/Field';
import { Gamepad2, Plus } from '../ui/icons';
import { Switch, TextInput } from '../ui/inputs';
import { useToasts } from '../ui/Toast';
import { useOrgAccess } from './org/orgAccess';
import { OrgTabs } from './org/OrgTabs';
import { LoadError, LoadingPane } from '../ui/PageStates';
import './org/org.css';

export function Games() {
  const { org = '' } = useParams();
  const { client } = useAuth();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [games, setGames] = useState<Game[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // the archived games too (spec/dashboard.md section 12.4): `?archived=1` in the URL, `includeArchived=true` to the API
  const [params, setParams] = useSearchParams();
  const showArchived = params.get('archived') === '1';
  const access = useOrgAccess(org);
  const toasts = useToasts();
  const [restoring, setRestoring] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [o, page] = await Promise.all([client.organization(org), client.games(org, showArchived)]);
      setOrganization(o);
      setGames(page.items);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (request ${e.requestId})` : 'Failed to load.');
    }
  }, [client, org, showArchived]);

  useEffect(() => {
    void load();
  }, [load]);

  const restore = async (g: Game) => {
    setRestoring(g.id);
    try {
      await client.call(ops.restoreGame, { path: { org, game: g.slug } });
      toasts.success(`${g.name} restored.`);
      await load();
    } catch (e) {
      toasts.error(e instanceof ApiError ? e.message : 'Could not restore the game.', { requestId: e instanceof ApiError ? e.requestId : null });
    } finally {
      setRestoring(null);
    }
  };

  const toggleArchived = (v: boolean) => {
    const next = new URLSearchParams(params);
    if (v) next.set('archived', '1');
    else next.delete('archived');
    setParams(next, { replace: true });
  };
  return (
    <section>
      <div className="page-head">
        <h1>Games</h1>
        {organization && <span className="page-sub">{organization.name}</span>}
        <div className="spacer" />
        <Button variant="primary" icon={<Plus aria-hidden="true" />} onClick={() => setCreating(true)}>
          New game
        </Button>
      </div>
      <OrgTabs org={org} value="games" />
      <div className="page-toolbar">
        <Switch label="Show archived" aria-label="Show archived" checked={showArchived} onChange={toggleArchived} />
      </div>
      {error ? (
        <LoadError message={error} />
      ) : !games || !organization ? (
        <LoadingPane />
      ) : (
      <DataGrid
        id="games"
        height={480}
        caption={`Games of ${organization.name}`}
        rows={games}
        rowKey={(g) => g.id}
        onRowOpen={(g) => navigate(`/orgs/${org}/games/${g.slug}`)}
        openOnClick
        emptyState={
          <EmptyState
            icon={<Gamepad2 />}
            title="No games yet"
            body={showArchived ? 'This organization has no games, archived or not.' : 'Create the first game of the organization; it starts with three environments and a main branch.'}
            action={<Button onClick={() => setCreating(true)}>Create a game</Button>}
          />
        }
        columns={[
          {
            key: 'name',
            header: 'Name',
            render: (g) => (
              <>
                <Link to={`/orgs/${org}/games/${g.slug}`}>{g.name}</Link> {g.archivedAt && <Badge tone="warning">archived</Badge>}
              </>
            ),
            cellText: (g) => g.name,
            sortValue: (g) => g.name,
          },
          { key: 'slug', header: 'Slug', render: (g) => <code>{g.slug}</code> },
          { key: 'desc', header: 'Description', render: (g) => g.description ?? '' },
          { key: 'created', header: 'Created', render: (g) => formatInstantWithZone(g.createdAt) },
          ...(showArchived
            ? [
                {
                  key: 'restore',
                  header: '',
                  render: (g: Game) =>
                    g.archivedAt ? (
                      <Button size="s" onClick={() => void restore(g)} busy={restoring === g.id} missingPermission={access.manages ? null : 'manage_settings'}>
                        Restore
                      </Button>
                    ) : null,
                },
              ]
            : []),
        ]}
      />
      )}
      <CreateGameModal
        open={creating}
        org={org}
        onClose={() => setCreating(false)}
        onCreated={(g) => {
          setCreating(false);
          navigate(`/orgs/${org}/games/${g.slug}`);
        }}
      />
    </section>
  );
}

function CreateGameModal({ open, org, onClose, onCreated }: { open: boolean; org: string; onClose: () => void; onCreated: (g: Game) => void }) {
  const { client } = useAuth();
  const formId = useId();
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  // one idempotency key per modal opening: a double submit or a retry after a network error cannot create two games
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

  useEffect(() => {
    if (open) {
      setIdempotencyKey(crypto.randomUUID());
      setError(null);
    }
  }, [open]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onCreated(await client.createGame(org, { slug, name }, idempotencyKey));
      setSlug('');
      setName('');
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, 'internal_error', 'Something went wrong.', ''));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="New game"
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} variant="primary" busy={busy}>
            Create
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit}>
        <Field label="Name" error={error?.fieldMessage('name')}>
          <TextInput value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <Field label="Slug" help="Lowercase letters, digits and dashes." error={error?.fieldMessage('slug')}>
          <TextInput className="mono" value={slug} onChange={(e) => setSlug(e.target.value)} pattern="[a-z0-9][a-z0-9-]{1,62}" required />
        </Field>
        {error && !error.details?.length && <LoadError message={error.message} />}
        <p className="muted org-note">Three environments (dev, stage, prod) and the main branch are created with the game.</p>
      </form>
    </Modal>
  );
}
